import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import type { ReportRowOut, TaxonomyTerm } from "../api/types";
import { Icon } from "../components/Icon";
import { TaxonomyText } from "../components/TaxonomyText";
import { useLang, LANGUAGE_NAME } from "../stores/language";
import { trackEvent } from "../telemetry";

export function ReportPage() {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const viewedAnalysisId = useRef<string | null>(null);
  const lang = useLang();

  const analysisQuery = useQuery({
    queryKey: ["latest-analysis", docId],
    queryFn: () => api.latestAnalysis(docId!),
    enabled: !!docId,
  });
  const analysisId = analysisQuery.data?.id;
  const reportQuery = useQuery({
    queryKey: ["report", analysisId, lang],
    queryFn: () => api.getReport(analysisId!, lang),
    enabled: !!analysisId,
    placeholderData: (previous) => previous,
  });
  const finalize = useMutation({
    mutationFn: () => api.finalize(analysisId!),
    onMutate: () => {
      trackEvent("report_finalize_requested", {
        document_id: docId,
        analysis_id: analysisId,
      });
    },
    onSuccess: () => {
      trackEvent("report_finalized", {
        document_id: docId,
        analysis_id: analysisId,
      });
      qc.invalidateQueries({ queryKey: ["latest-analysis", docId] });
      qc.invalidateQueries({ queryKey: ["report", analysisId] });
    },
    onError: () => {
      trackEvent("report_finalize_failed", {
        document_id: docId,
        analysis_id: analysisId,
      });
    },
  });

  useEffect(() => {
    const report = reportQuery.data;
    if (!report || viewedAnalysisId.current === report.analysis_id) return;
    viewedAnalysisId.current = report.analysis_id;
    trackEvent("report_viewed", {
      document_id: docId,
      analysis_id: report.analysis_id,
      row_count: report.rows.length,
      finalized: Boolean(report.finalized_at),
    });
  }, [docId, reportQuery.data]);

  if (analysisQuery.isLoading || reportQuery.isLoading) {
    return <div className="card card-pad"><Icon name="refresh" size={14} className="spin" /> Loading report…</div>;
  }
  if (analysisQuery.error || reportQuery.error || !reportQuery.data) {
    return <div className="card card-pad">The report is not available yet. Return to review and approve all rows.</div>;
  }

  const report = reportQuery.data;
  const finalized = !!report.finalized_at || !!analysisQuery.data?.finalized_at;

  return (
    <div className="report-page">
      <div className="page-head">
        <div>
          <div className="row-eyebrow">Expert report</div>
          <h2 className="page-title">{report.document_filename}</h2>
          <div className="page-sub">Analysis {report.analysis_id} · sources retained for every row</div>
        </div>
        <div className="report-actions">
          <button
            className="btn btn-secondary"
            onClick={() => {
              trackEvent("report_edit_review_selected", {
                document_id: docId,
                analysis_id: report.analysis_id,
              });
              navigate(`/docs/${docId}/wizard/${report.rows[0]?.row_key ?? ""}`);
            }}
          >
            Edit review
          </button>
          <a
            className="btn btn-secondary"
            href={`/api/v1/analyses/${report.analysis_id}/report.html?lang=${lang}`}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackEvent("report_html_opened", {
                document_id: docId,
                analysis_id: report.analysis_id,
              })
            }
          >
            Printable HTML
          </a>
          <button className="btn btn-primary" disabled={finalized || finalize.isPending} onClick={() => finalize.mutate()}>
            <Icon name="check" size={14} /> {finalized ? "Finalized" : "Finalize report"}
          </button>
        </div>
      </div>

      {reportQuery.isFetching && lang !== report.text_language ? (
        <div className="translation-note" role="note">
          <Icon name="refresh" size={14} className="spin" /> Translating…
        </div>
      ) : report.machine_translated ? (
        <div className="translation-note" role="note">
          <Icon name="info" size={14} />{" "}
          {`Model text is machine-translated from the ${LANGUAGE_NAME[report.output_language]} original. Switch to ${report.output_language.toUpperCase()} to see it.`}
        </div>
      ) : (
        lang !== report.text_language && (
          <div className="translation-note" role="note">
            <Icon name="info" size={14} /> Translation is unavailable right now; showing the original text.
          </div>
        )
      )}

      {finalize.error && <div className="card card-pad report-error">Finalization failed: {String(finalize.error)}</div>}
      {finalized && <div className="card card-pad report-finalized">Finalized {new Date(report.finalized_at ?? analysisQuery.data!.finalized_at!).toLocaleString()}</div>}

      <div className="report-list">
        {report.rows.map((row) => (
          <ReportRow
            key={row.row_key}
            row={row}
            terms={report.terms}
            onEdit={() => navigate(`/docs/${docId}/wizard/${row.row_key}`)}
          />
        ))}
      </div>
    </div>
  );
}

function ReportRow({
  row,
  terms,
  onEdit,
}: {
  row: ReportRowOut;
  terms: TaxonomyTerm[];
  onEdit: () => void;
}) {
  const lang = useLang();
  return (
    <article className="card report-row-card">
      <header className="report-row-head">
        <div>
          <div className="row-eyebrow">Analysis item</div>
          <h3>{lang === "pl" ? row.subject_pl : row.subject_en}</h3>
          <div className="card-sub">
            {lang === "pl" ? row.subject_en : row.subject_pl}
          </div>
        </div>
        <span className={`risk-banner risk-${row.risk_level}`}>{row.risk_level}</span>
        <span className={`assessment-badge assessment-${row.assessment_status}`}>
          {row.assessment_status.replaceAll("_", " ")}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={onEdit} title="Edit this row">
          Edit
        </button>
      </header>
      <div className="report-row-grid">
        <section>
          <div className="field-label">Final description</div>
          <div className="field-val"><TaxonomyText text={row.description} terms={terms} /></div>
          <div className="field-label mt-3">Report comment</div>
          <div className="field-val"><TaxonomyText text={row.comments} terms={terms} /></div>
        </section>
        <aside className="report-sources">
          <div className="field-label">Source</div>
          <strong>{row.source.label}</strong>
          <div className="card-sub">Pages: {row.source.pages.join(", ") || "not recorded"}</div>
          {row.source.quotes_pl.length > 0 ? row.source.quotes_pl.map((quote, index) => (
            <blockquote key={index}><TaxonomyText text={quote} terms={terms} /></blockquote>
          )) : <div className="card-sub">No source quote recorded.</div>}
        </aside>
      </div>
    </article>
  );
}
